import { useTranslations } from '@fshr/context/i18n-context';
import BevSafeText from '../bev-safe-text';
import { ArrowRight } from '../icon/arrow-right';
import Image from '../image';
import { LinkSectionWrapper } from '../for-you/link-section-wrapper';
import { IconLocationPin } from '../icon';
import { HighlightedRecommendation } from '../types';
import { useRecoTracking } from '../for-you/analytics/recoTracker';
import { useRecoImpressionBatchObserver } from '../for-you/hooks/useRecoImpression';
import { useMemo } from 'react';

/**
 * @description Status config for the product card
 * @param status - The status of the product card
 * @returns The color and text for the status
 */
const statusConfig: Record<
	'available' | 'partially-available' | 'not-available',
	{ color: string; text: string }
> = {
	available: { color: '#2ECC40', text: 'Available' },
	'partially-available': { color: '#FFD600', text: 'Some dates avail...' },
	'not-available': { color: '#FF3B30', text: 'Not available' },
};

const StatusIndicator = ({
	status,
}: {
	status?: 'available' | 'partially-available' | 'not-available';
}) => {
	if (!status) return null;

	const { color, text } = statusConfig[status];
	return (
		<div
			className="absolute left-0 right-0 bottom-0 w-full flex justify-center items-center z-1 px-[5px] pt-[6px] pb-[4px] gap-[6px]"
			style={{
				background: 'rgba(3, 3, 3, 0.6)',
				backdropFilter: 'blur(8px)',
				boxShadow: '0 4px 30px rgba(0,0,0,0.3)',
			}}
		>
			<span
				className="inline-block mb-[5px]"
				style={{
					width: 7,
					height: 7,
					borderRadius: '50%',
					background: color,
				}}
			/>
			<p className="text-caption text-text-on-image-primary line-clamp-1">{text}</p>
		</div>
	);
};

const isValidStatus = (
	status?: string
): status is 'available' | 'partially-available' | 'not-available' => {
	if (!status) {
		return false;
	}

	return ['available', 'not-available', 'partially-available'].includes(status);
};

const ImageSection = ({
	image,
	status,
	altText,
}: {
	image: string;
	status?: string;
	altText?: string;
}) => {
	const t = useTranslations();
	return (
		<div className="w-full flex flex-row gap-[15px] items-start justify-start">
			<div className="relative w-full h-[243px] min-w-full min-h-[243px] md:w-[355px] md:h-[188px] md:min-w-[485px] md:min-h-[270px] flex-shrink-0">
				<Image
					roughSize="m"
					src={image}
					className="h-full w-full object-cover"
					alt={altText ?? t('Highlighted Recommendation')}
				/>
				{isValidStatus(status) ? <StatusIndicator status={status} /> : null}
			</div>
		</div>
	);
};

const LocationSection = ({ location }: { location?: string }) => {
	return location ? (
		<div className="flex flex-row gap-[5px]">
			<IconLocationPin />
			<p className="text-caption text-text-heading">{location}</p>
		</div>
	) : null;
};

export function HighlightedRecommendationCard({
	recommendation,
	placement,
	position,
	trackRecoImpressions = true,
}: {
	recommendation?: HighlightedRecommendation;
	placement: string;
	position?: number;
	trackRecoImpressions?: boolean;
}) {
	const t = useTranslations();
	const { trackEnhancementRecoClick, trackPropertyRecoClick } = useRecoTracking();
	const s = (v?: string | number | null) => (v !== undefined && v !== null ? String(v) : 'na');
	const recoPayload: any = recommendation
		? placement !== 'for_you-highlighted_property'
			? {
					reco_enhancement_name: s(recommendation.title),
					reco_enhancement_sku: s(recommendation.sku) || 'na',
					reco_enhancement_property_name: s(recommendation.propertyOrsCode?.toLowerCase()) || 'na',
					reco_placement: placement,
					reco_position: s(position),
					enhancement_datalake_reco_id: s(recommendation.datalakeRecoId),
				}
			: {
					reco_property_name: s(recommendation.propertyOrsCode?.toLowerCase()),
					reco_property_code: s(recommendation.propertyOrsCode?.toLowerCase()),
					reco_placement: placement,
					reco_position: s(position),
					reco_room_package_code_array: 'na',
					reco_room_code_array: 'na',
					property_datalake_reco_id: s(recommendation.datalakeRecoId),
				}
		: null;

	const impressionRef = useRecoImpressionBatchObserver(
		recoPayload,
		placement !== 'for_you-highlighted_property' ? 'ancillary' : 'property',
		trackRecoImpressions
	);
	if (!recommendation) return null;

	const {
		image,
		title,
		type,
		status,
		url,
		location,
		sku,
		propertyOrsCode,
		datalakeRecoId,
		propertyName,
		category,
		subcategory,
	} = recommendation;

	return (
		<LinkSectionWrapper
			ref={impressionRef}
			url={url}
			propertyOrsCode={propertyOrsCode}
			sku={sku}
			datalakeRecoId={datalakeRecoId}
			className="w-full flex flex-col spacing-stack-xs"
			onClick={() => {
				if (placement !== 'for_you-highlighted_property') {
					trackEnhancementRecoClick({
						sku: sku || 'na',
						name: title,
						propertyName: propertyName || 'na',
						propertyCode: propertyOrsCode || 'na',
						enhancementDatalakeRecoId: datalakeRecoId || 'na',
						placement,
						position,
					});
				} else {
					trackPropertyRecoClick({
						propertyCode: propertyOrsCode ?? 'na',
						propertyName: propertyName ?? 'na',
						propertyDatalakeRecoId: datalakeRecoId ?? 'na',
						placement,
						position,
					});
				}
			}}
		>
			<ImageSection image={image.url} status={status} altText={image.altText} />
			<div className="flex flex-col w-full spacing-stack-2xs">
				<p className="text-overline text-text-primary">{type}</p>
				<div className="flex flex-row spacing-inline-xs items-center">
					<h3 className="text-subtitle1">
						<span className="text-text-primary">
							<BevSafeText>{title}</BevSafeText>
						</span>
					</h3>
					<div className="pt-[1px]" aria-label={t('Navigate to ancilliary landing page')}>
						<ArrowRight width={15} height={15} />
					</div>
				</div>
				<LocationSection location={location} />
			</div>
		</LinkSectionWrapper>
	);
}
